import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { getLevelType } from 'shared-utils/src/createSearchSummary/getWhereSummary';

import { CtxContainer } from '@client/app/Container';
import { queryBuilder } from '@modals/factories';

import Locations from './components/Locations';
import Map from './components/Map';

const propTypes = {
  mainLocation: PropTypes.instanceOf(Object),
  filters: PropTypes.instanceOf(Object),
  nearby: PropTypes.instanceOf(Object),
  closeSearch: PropTypes.func,
  setLocations: PropTypes.func,
  setCurrentNearby: PropTypes.func,
  localitiesWithAgencyZones: PropTypes.instanceOf(Array),
  setMapDialog: PropTypes.func,
  submitSearch: PropTypes.func,
};

const defaultProps = {
  mainLocation: null,
  filters: {},
  nearby: null,
  closeSearch: () => { },
  setLocations: () => { },
  setCurrentNearby: () => { },
  localitiesWithAgencyZones: [],
  setMapDialog: () => { },
  submitSearch: () => { },
};

export const CtxSearch = React.createContext();
export const useCtxSearch = () => useContext(CtxSearch);

function getSearchText(mainLocation, additionalLocations, { geocircle, geopolygon, geobounds }) {
  let text = {
    prefix: <>a</>,
    prefixText: 'a',
    label: 'Scegli dove',
    additional: null,
    ariaLabel: 'Scegli la località dove vuoi cercare',
  };

  if (mainLocation) {
    const mainLocLevelType = getLevelType(mainLocation.level);
    let mainLocText = `${mainLocation.name}${mainLocLevelType === 'province' ? ' (prov.)' : ''}`;
    let mainLocPrefix = mainLocLevelType === 'metro_line' ? 'sulla' : 'a';
    let ariaLabel = `${mainLocPrefix} ${mainLocation.name}${mainLocLevelType === 'province' ? ' (prov.)' : ''}`;
    if (mainLocation.level === 999) {
      if (mainLocation.radius) {
        mainLocPrefix = `entro ${mainLocation.radius} da`;
        mainLocText = <span style={{ display: 'block' }} className="tp--ell">{mainLocation.streetName}, {mainLocation.city}</span>;
        ariaLabel = `Entro ${mainLocation.radius} metri da ${mainLocation.streetName}, ${mainLocation.city}`;
      } else if (mainLocation.travelTime) {
        mainLocPrefix = `a ${mainLocation.travelTime} ${mainLocation.travelMode} da`;
        mainLocText = <span style={{ display: 'block' }} className="tp--ell">{mainLocation.streetName}, {mainLocation.city}</span>;
        ariaLabel = `A ${mainLocation.travelTime} ${mainLocation.travelMode} da ${mainLocation.streetName}, ${mainLocation.city}`;
      } else if (geopolygon) {
        mainLocPrefix = 'nella tua';
        mainLocText = <span style={{ display: 'block' }} className="tp--ell">{`${mainLocation.name}`}</span>;
        ariaLabel = 'a tua area disegnata';
      } else if (geobounds) {
        mainLocPrefix = 'nella tua';
        mainLocText = <span style={{ display: 'block' }} className="tp--ell">{`${mainLocation.name}`}</span>;
        ariaLabel = 'area su mappa';
      }

      return {
        prefix: mainLocPrefix,
        label: mainLocText,
        ariaLabel,
      };
    }

    if (additionalLocations && additionalLocations.length) {
      let additionaLocationsTypeText = '';
      const [first, ...rest] = additionalLocations;
      switch (mainLocLevelType) {
        case 'province':
          additionaLocationsTypeText = `ne${additionalLocations.length > 1 ? 'i' : 'l'} comun${additionalLocations.length > 1 ? 'i' : 'e'}:`;
          break;
        case 'city':
          additionaLocationsTypeText = `nell${additionalLocations.length > 1 ? 'e' : 'a'} zon${additionalLocations.length > 1 ? 'e' : 'a'}:`;
          break;
        case 'metro_line':
          additionaLocationsTypeText = `intorno all${additionalLocations.length > 1 ? 'e' : 'a'} fermat${additionalLocations.length > 1 ? 'e' : 'a'}:`;
          break;
        default:
          additionaLocationsTypeText = `nell${additionalLocations.length > 1 ? 'e' : 'a'} zon${additionalLocations.length > 1 ? 'e' : 'a'}:`;
      }

      const label = first.name.length > 40 ? `${first.name.substring(0, 37)}...` : first.name;

      text = {
        prefix: `${mainLocPrefix} ${mainLocText} ${additionaLocationsTypeText}`,
        prefixText: `${mainLocPrefix} ${mainLocText} ${additionaLocationsTypeText}`,
        label: <>{label}</>,
        additional: rest.length > 0 ? ` +${rest.length}` : null,
        ariaLabel: `${ariaLabel} ${additionaLocationsTypeText} ${first.name}, ${rest.map((loc) => loc.name).join(', ')}`,
      };
    } else {
      text = {
        prefix: <>{mainLocPrefix}</>,
        prefixText: mainLocPrefix,
        label: mainLocText,
        additional: null,
        ariaLabel,
      };
    }
  }

  return text;
}

const Locality = React.memo(function Locality({
  mainLocation,
  additionalLocations,
  filters: { geocircle, geopolygon, geobounds, qExpansion, nearby },
  setLocations,
  setCurrentNearby,
  localitiesWithAgencyZones,
  submitSearch,
}) {
  const {
    isMobile,
    isSmallMobile,
    openModal,
    filtersProps,
    filtersProps: { isAgencyHome },
    pageType,
  } = useContext(CtxContainer);

  const text = getSearchText(mainLocation, additionalLocations, { geocircle, geopolygon, geobounds });

  const openQB = ({ actionClicked }) => {
    const qbConf = {
      isMobile,
      isSmallMobile,
      pageType,
      isAgency: isAgencyHome,
      localitiesWithAgencyZones,
      setLocations,
      setCurrentNearby,
      filtersProps,
      submitSearch,
      panelChooseBy: {
        isMobile,
        actionClicked,
        pageFrom: pageType,
      },
    };

    openModal && openModal(queryBuilder(qbConf));
  };

  const startLocalitySearch = () => openQB({ actionClicked: isAgencyHome ? 'agzMainLocalityBox' : 'mainLocalityBox' });

  const startLocalitySearchOnKeyPress = (e) => {
    if (e.key === 'Enter') {
      startLocalitySearch();
    }
  };

  const editQExpansion = () => openQB({ actionClicked: 'editQExpansion' });

  const editQExpansionOnKeyPress = (e) => {
    if (e.key === 'Enter') {
      editQExpansion();
    }
  };  
  
  if (mainLocation?.level === 999) {
    return (
    <Map
      isMobile={isMobile}
      filters={{ geocircle, geopolygon, geobounds, nearby }}
      text={text}
      startLocalitySearch={startLocalitySearch}
      startLocalitySearchOnKeyPress={startLocalitySearchOnKeyPress}
    />
  );
  } else {
    return (
      <Locations
        mainLocation={mainLocation}
        additionalLocations={additionalLocations}
        qExpansion={qExpansion}
        editQExpansion={editQExpansion}
        editQExpansionOnKeyPress={editQExpansionOnKeyPress}
        startLocalitySearch={startLocalitySearch}
        startLocalitySearchOnKeyPress={startLocalitySearchOnKeyPress}
        isMobile={isMobile}
        text={text}
      />
    );
  }
});

Locality.propTypes = propTypes;
Locality.defaultProps = defaultProps;
export default Locality;
